import {
  Baby,
  CreditCard,
  BarChart3,
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  FolderKanban,
  GraduationCap,
  LayoutDashboard,
  Library,
  Megaphone,
  MessagesSquare,
  Bell,
  NotebookPen,
  Settings,
  KeyRound,
  ShieldAlert,
  TableProperties,
  Users,
  type LucideIcon,
} from 'lucide-react';

import type { Role } from '@/stores/session';

/** A single navigable link. `labelKey` resolves in the `nav` i18n namespace. */
export interface NavItem {
  labelKey: string;
  to: string;
  icon: LucideIcon;
  /** Omitted ⇒ visible to every authenticated role. */
  roles?: Role[];
  /** Extra path prefixes that light this item up in the SIDEBAR — for deep
   *  routes that live under another URL root (a lesson under Курси). Purely
   *  visual: navItemForPath/canRoleVisit ignore these, so role gating and
   *  post-login redirects are untouched. */
  activePrefixes?: string[];
}

export const BRAND_NAME = 'Osvitly';

/**
 * Phase 1–4 navigation: Dashboard (everyone), Courses & Calendar & Classes &
 * Tests & Homework (admin/teacher/pupil — the assessment APIs 403 for
 * parents), My children (parent — the ParentLink-scoped submissions read),
 * Gradebook & Attendance (admin/teacher — the progress read models are
 * staff-only), My progress (pupil), Projects (admin/teacher/pupil — parents 403 on the whole surface), Library
 * (EVERY role incl. parent — the PRD's explicit parent read), Members
 * (admin/teacher), Settings (admin). Grows into grouped sections as
 * business features land — follow the template's NavSection/NavGroup pattern
 * from dev13-spa when it does.
 */
export const navItems: NavItem[] = [
  { labelKey: 'dashboard', to: '/', icon: LayoutDashboard },
  {
    labelKey: 'courses',
    to: '/courses',
    icon: BookOpen,
    roles: ['admin', 'teacher', 'pupil'],
    // Lessons live under Курси even though their URLs sit at the root.
    activePrefixes: ['/lessons'],
  },
  {
    labelKey: 'calendar',
    to: '/calendar',
    icon: CalendarDays,
    roles: ['admin', 'teacher', 'pupil'],
  },
  {
    labelKey: 'classes',
    to: '/classes',
    icon: GraduationCap,
    roles: ['admin', 'teacher', 'pupil'],
  },
  {
    labelKey: 'tests',
    to: '/tests',
    icon: ClipboardList,
    roles: ['admin', 'teacher', 'pupil'],
    // Taking, results and grading are all "Тести" work.
    activePrefixes: ['/attempts'],
  },
  {
    labelKey: 'homework',
    to: '/homework',
    icon: NotebookPen,
    roles: ['admin', 'teacher', 'pupil'],
  },
  {
    labelKey: 'projects',
    to: '/projects',
    icon: FolderKanban,
    roles: ['admin', 'teacher', 'pupil'],
  },
  // Every known role, spelled out rather than omitted: an omitted `roles`
  // means "any authenticated role", which would let an unrecognised one
  // through (deny-by-default, FE-ADR-005).
  {
    labelKey: 'chats',
    to: '/chats',
    icon: MessagesSquare,
    roles: ['admin', 'teacher', 'pupil', 'parent'],
  },
  {
    labelKey: 'announcements',
    to: '/announcements',
    icon: Megaphone,
    roles: ['admin', 'teacher', 'pupil', 'parent'],
  },
  {
    labelKey: 'notifications',
    to: '/notifications',
    icon: Bell,
    roles: ['admin', 'teacher', 'pupil', 'parent'],
  },
  {
    labelKey: 'library',
    to: '/library',
    icon: Library,
    roles: ['admin', 'teacher', 'pupil', 'parent'],
  },
  {
    labelKey: 'gradebook',
    to: '/gradebook',
    icon: TableProperties,
    roles: ['admin', 'teacher'],
    // Staff open a pupil's progress from the gradebook matrix.
    activePrefixes: ['/progress/pupils'],
  },
  {
    labelKey: 'attendance',
    to: '/attendance',
    icon: ClipboardCheck,
    roles: ['admin', 'teacher'],
  },
  { labelKey: 'myProgress', to: '/progress/me', icon: BarChart3, roles: ['pupil'] },
  {
    labelKey: 'children',
    to: '/children',
    icon: Baby,
    roles: ['parent'],
    // Parents reach the same pupil-progress page from «Мої діти»; the sidebar
    // resolves per role, so this never clashes with the gradebook claim.
    activePrefixes: ['/progress/pupils'],
  },
  { labelKey: 'members', to: '/members', icon: Users, roles: ['admin', 'teacher'] },
  { labelKey: 'moderation', to: '/moderation', icon: ShieldAlert, roles: ['admin'] },
  { labelKey: 'settings', to: '/settings', icon: Settings, roles: ['admin'] },
  // A personal MCP token is admin/teacher only: a key can never exceed its
  // owner's role, so pupils and parents never get one (backend ADR-023).
  { labelKey: 'apiKeys', to: '/settings/api-keys', icon: KeyRound, roles: ['admin', 'teacher'] },
  // Billing is the admin's concern (owner's call, 2026-09-07): pupils and
  // teachers never see the page — the trial/locked state still reaches staff
  // through the dashboard card and the school_locked handler.
  {
    labelKey: 'billing',
    to: '/billing',
    icon: CreditCard,
    roles: ['admin'],
  },
];

export const visibleForRole = (roles: Role[] | undefined, role: Role | null): boolean =>
  !roles || (role !== null && roles.includes(role));

/** Filter the nav for the active membership role (deny-by-default). */
export function navItemsForRole(role: Role | null): NavItem[] {
  return navItems.filter(item => visibleForRole(item.roles, role));
}

/**
 * The nav entry that governs `pathname` — the LONGEST `to` that is either the
 * path itself or a parent segment of it.
 *
 * Longest wins because the prefixes nest: `/settings/api-keys` (admin AND
 * teacher) must not be judged by `/settings` (admin only). `/` matches only
 * itself, since as a prefix it would match everything.
 */
export function navItemForPath(pathname: string): NavItem | undefined {
  const path = pathname.replace(/\/+$/, '') || '/';
  let best: NavItem | undefined;
  for (const item of navItems) {
    const governs =
      item.to === '/' ? path === '/' : path === item.to || path.startsWith(`${item.to}/`);
    if (governs && (!best || item.to.length > best.to.length)) best = item;
  }
  return best;
}

/**
 * May `role` visit `pathname`? Answered from the nav — the app's single
 * declarative route→roles table — so there is never a second one to keep in
 * step with it.
 *
 * A path no nav entry governs (`/invite?token=…`, `/join`, a detail route
 * living under no nav item) passes: the route's own `<RequireRole>` guard is
 * the authority, and refusing an unrecognised path here would break exactly
 * the deep links that are worth preserving across a sign-in.
 */
export function canRoleVisit(pathname: string, role: Role | null): boolean {
  const item = navItemForPath(pathname);
  return item ? visibleForRole(item.roles, role) : true;
}

/** Where a role lands when it has nowhere better to go: the first nav entry it
 *  can actually see (the dashboard, for every role the UI knows). */
export function homePathForRole(role: Role | null): string {
  return navItemsForRole(role)[0]?.to ?? '/';
}
